import {
  ColumnDef,
  ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
  VisibilityState,
} from '@tanstack/react-table';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AiOutlineArrowLeft, AiOutlineDownload, AiOutlineReload } from 'react-icons/ai';
import { FiChevronDown, FiChevronLeft, FiChevronRight, FiCopy, FiFilter, FiSearch } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';

import { Button } from '~/components/shadcn/button-simple';
import { Card, CardContent, CardHeader, CardTitle } from '~/components/shadcn/card-simple';
import { Input } from '~/components/shadcn/input-simple';
import { Label } from '~/components/shadcn/label-simple';
import { Select } from '~/components/shadcn/select-simple';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '~/components/shadcn/table-simple';
import api from '~/services/api';
import copyTextToClipboard from '~/utils/copyTextToClipboard';

import { BatteryListByPNProps, BatteryListResponse, BatteryRow, InventoryItemHeader } from './types';

const DEFAULT_STATUS_FILTER = ['ToBeInspected', 'InStock', 'Failed'];
const DEFAULT_CONDITION_FILTER = ['New', 'Used'];
const STATUS_OPTIONS = [
  'InUse',
  'ToBeInspected',
  'ToBeInspected-RMA',
  'Downstream',
  'AwaitingShipment',
  'Sold',
  'Returning',
  'Failed',
  'Returned',
  'Allocated',
  'InStock',
];
const CONDITION_OPTIONS = ['New', 'Used'];

function clampHealthValue(value: number) {
  if (Number.isNaN(value)) return 0;
  if (value < 0) return 0;
  if (value > 100) return 100;
  return value;
}

function normalizeSerialInput(value: string) {
  const normalizedValue = value.trim();

  if (normalizedValue.substring(0, 4) === 'P/N:') {
    return normalizedValue.substring(normalizedValue.indexOf('S/N:') + 4).trim();
  }

  if (normalizedValue.includes('S/N:')) {
    return normalizedValue.substring(normalizedValue.indexOf('S/N:') + 4).trim();
  }

  return normalizedValue;
}

function parseBatteryValue(value: string | number | null) {
  if (value === null) return null;
  if (typeof value === 'number') return value;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
}

function getSortDirectionLabel(direction: false | 'asc' | 'desc') {
  if (direction === 'asc') return 'ASC';
  if (direction === 'desc') return 'DESC';
  return '';
}

function csvEscape(value: string | number | null | undefined) {
  const safeValue = value ?? '';
  const asString = String(safeValue);

  if (asString.includes(',') || asString.includes('"') || asString.includes('\n')) {
    return `"${asString.replace(/"/g, '""')}"`;
  }

  return asString;
}

function exportRowsToCsv(rows: BatteryRow[], fileName: string) {
  const headers = ['Serial Number', 'Battery Health', 'Battery File', 'Status', 'Condition', 'Location', 'Notes'];

  const lines = rows.map((row) => {
    let batteryHealth = '';
    if (row.condition === 'New') {
      batteryHealth = '100%';
    } else if (row.battery === null) {
      batteryHealth = '';
    } else {
      batteryHealth = `${row.battery}%`;
    }
    const locationLabel = `${row.locations?.name ?? ''} ${row.usersLocation?.name ?? ''}`.trim();

    return [
      row.serial_number,
      batteryHealth,
      row.battery_file?.name ?? '',
      row.status,
      row.condition,
      locationLabel,
      row.description ?? '',
      row.processed_by ?? '',
    ]
      .map(csvEscape)
      .join(',');
  });

  const csv = [headers.join(','), ...lines].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const href = URL.createObjectURL(blob);
  const anchor = document.createElement('a');

  anchor.href = href;
  anchor.setAttribute('download', fileName);
  document.body.appendChild(anchor);
  anchor.click();

  document.body.removeChild(anchor);
  URL.revokeObjectURL(href);
}

export function BatteryListByPN({ match, location }: BatteryListByPNProps) {
  const [inventoryItemHeader, setInventoryItemHeader] = useState<InventoryItemHeader>({
    item_description: '',
    item_sku: '',
    branch: 1,
  });
  const [inventoryItems, setInventoryItems] = useState<BatteryRow[]>([]);
  const [searchQuery, setSearchQuery] = useState<string | null>(null);
  const [zeroQty, setZeroQty] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState<string[]>(DEFAULT_STATUS_FILTER);
  const [filterCondition, setFilterCondition] = useState<string[]>(DEFAULT_CONDITION_FILTER);
  const [filterHealthRange, setFilterHealthRange] = useState<[number, number]>([0, 100]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [paginationTotalItems, setPaginationTotalItems] = useState(0);
  const [paginationSize, setPaginationSize] = useState(25);
  const [paginationPage, setPaginationPage] = useState(1);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [showStatusFilters, setShowStatusFilters] = useState(false);
  const [showConditionFilters, setShowConditionFilters] = useState(false);
  const [showColumnMenu, setShowColumnMenu] = useState(false);
  const statusFilterRef = useRef<HTMLDivElement | null>(null);

  const itemId = match.params.item_id;

  const sortOrder = useMemo(() => {
    const batterySort = sorting.find((sort) => sort.id === 'battery');

    if (!batterySort) return undefined;
    return batterySort.desc ? 'descend' : 'ascend';
  }, [sorting]);

  const hasActiveFilters = useMemo(() => {
    const hasDefaultStatus = filterStatus.length === DEFAULT_STATUS_FILTER.length && DEFAULT_STATUS_FILTER.every((status) => filterStatus.includes(status));
    const hasDefaultCondition =
      filterCondition.length === DEFAULT_CONDITION_FILTER.length && DEFAULT_CONDITION_FILTER.every((condition) => filterCondition.includes(condition));
    const hasDefaultHealth = filterHealthRange[0] === 0 && filterHealthRange[1] === 100;
    const hasSerialSearch = appliedSearch.trim().length > 0;

    return !(hasDefaultStatus && hasDefaultCondition && hasDefaultHealth && sorting.length === 0 && !hasSerialSearch);
  }, [appliedSearch, filterCondition, filterHealthRange, filterStatus, sorting]);

  const columns = useMemo<ColumnDef<BatteryRow>[]>(
    () => [
      {
        accessorKey: 'serial_number',
        header: 'Serial Number',
        size: 180,
        minSize: 140,
        maxSize: 280,
        cell: ({ row }) => {
          const serialNumber = row.original.serial_number;

          const handleCopySerial = async () => {
            const copied = await copyTextToClipboard(serialNumber);

            if (copied) {
              toast.success('Serial number copied');
            } else {
              toast.error('Unable to copy serial number');
            }
          };

          return (
            <div className="flex items-center gap-2">
              <a href={`/inventoryProcessing/${row.original.id}`} target="_blank" rel="noreferrer" className="text-primary underline-offset-4 hover:underline">
                {serialNumber}
              </a>
              <button
                type="button"
                onClick={handleCopySerial}
                aria-label={`Copy serial number ${serialNumber}`}
                title="Copy serial number"
                className="rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                <FiCopy className="h-3.5 w-3.5" />
              </button>
            </div>
          );
        },
      },
      {
        id: 'battery',
        accessorFn: (row) => row.battery,
        header: () => <span>Battery Health</span>,
        size: 140,
        minSize: 120,
        maxSize: 200,
        sortingFn: (a, b) => {
          const aValue = parseBatteryValue(a.original.battery);
          const bValue = parseBatteryValue(b.original.battery);

          if (aValue === bValue) return 0;
          if (aValue === null) return 1;
          if (bValue === null) return -1;
          return aValue - bValue;
        },
        cell: ({ row }) => {
          if (row.original.condition === 'New') return '100%';
          if (row.original.battery === null) return '';
          return `${row.original.battery}%`;
        },
      },
      {
        id: 'battery_file',
        accessorFn: (row) => row.battery_file?.name ?? '',
        header: 'Battery File',
        size: 220,
        minSize: 180,
        maxSize: 300,
        cell: ({ row }) => {
          const file = row.original.battery_file;

          if (!file) return '';
          return (
            <a href={file.url} target="_blank" rel="noreferrer" className="text-primary underline-offset-4 hover:underline">
              {file.name}
            </a>
          );
        },
      },
      {
        accessorKey: 'status',
        header: 'Status',
        size: 170,
        minSize: 130,
        maxSize: 240,
      },
      {
        accessorKey: 'condition',
        header: 'Condition',
        size: 120,
        minSize: 90,
        maxSize: 170,
      },
      {
        id: 'location',
        accessorFn: (row) => `${row.locations?.name ?? ''} ${row.usersLocation?.name ?? ''}`.trim(),
        header: 'Location',
        size: 220,
        minSize: 160,
        maxSize: 320,
        cell: ({ row }) => {
          const locationName = row.original.locations?.name ?? '';
          const userLocation = row.original.usersLocation?.name ?? '';
          const fullDescription = `${row.original.locations?.description ?? ''} ${userLocation}`.trim();
          const label = `${locationName} ${userLocation}`.trim();

          return <span title={fullDescription}>{label}</span>;
        },
      },
      {
        accessorKey: 'description',
        header: 'Notes',
        size: 560,
        minSize: 220,
        maxSize: 900,
        cell: ({ row }) => {
          const description = row.original.description ?? '';
          return <span title={description}>{description}</span>;
        },
      },
    ],
    []
  );

  const table = useReactTable({
    data: inventoryItems,
    columns,
    state: {
      sorting,
      columnFilters,
      globalFilter,
      columnVisibility,
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onColumnVisibilityChange: setColumnVisibility,
    enableColumnResizing: true,
    columnResizeMode: 'onChange',
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  const fetchHeader = useCallback(async () => {
    try {
      const params = new URLSearchParams(location.search);
      const itemResponse = await api.get(`items/${itemId}`);

      setSearchQuery(params.get('searchQuery'));
      setZeroQty(params.get('showZero'));
      setInventoryItemHeader({
        item_description: `${itemResponse.data.part_number} - ${itemResponse.data.description}`,
        item_sku: itemResponse.data.sku,
        branch: 1,
      });
    } catch (error: any) {
      const message = error?.response?.data?.message || 'Unable to load item details.';
      toast.error(message);
      setErrorMessage(message);
    }
  }, [itemId, location.search]);

  const fetchInventory = useCallback(async () => {
    setLoading(true);
    setErrorMessage('');

    try {
      const normalizedSerial = normalizeSerialInput(appliedSearch);
      const queryParams = {
        serial_number: normalizedSerial || undefined,
        status: filterStatus.join(),
        condition: filterCondition.join(),
        size: paginationSize,
        page: paginationPage - 1,
        health_min: filterHealthRange[0],
        health_max: filterHealthRange[1],
        order: sortOrder,
      };

      const response = await api.get<BatteryListResponse>(`inventory_batteries/getInventoryBatteryListByPN/${itemId}`, {
        params: queryParams,
      });

      setInventoryItems(response.data.rows);
      setPaginationTotalItems(response.data.totalItems);
      setPaginationSize(response.data.sizePage);
      setPaginationPage(response.data.currentPage + 1);
    } catch (error: any) {
      const message = error?.response?.data?.message || 'Failed to load inventory batteries.';
      setErrorMessage(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [appliedSearch, filterCondition, filterHealthRange, filterStatus, itemId, paginationPage, paginationSize, sortOrder]);

  useEffect(() => {
    fetchHeader();
  }, [fetchHeader]);

  useEffect(() => {
    fetchInventory();
  }, [fetchInventory]);

  useEffect(() => {
    if (!showStatusFilters) return undefined;

    const handleOutsideClick = (event: MouseEvent) => {
      if (!statusFilterRef.current) return;
      if (statusFilterRef.current.contains(event.target as Node)) return;
      setShowStatusFilters(false);
    };

    document.addEventListener('mousedown', handleOutsideClick);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [showStatusFilters]);

  function applySearch() {
    setPaginationPage(1);
    setAppliedSearch(searchText);
  }

  function resetFilters() {
    setFilterStatus(DEFAULT_STATUS_FILTER);
    setFilterCondition(DEFAULT_CONDITION_FILTER);
    setFilterHealthRange([0, 100]);
    setSearchText('');
    setAppliedSearch('');
    setSorting([]);
    setPaginationPage(1);
  }

  function updateHealthMin(value: string) {
    const parsed = clampHealthValue(Number(value));
    setFilterHealthRange((currentRange) => {
      const max = Math.max(parsed, currentRange[1]);
      return [parsed, max];
    });
    setPaginationPage(1);
  }

  function updateHealthMax(value: string) {
    const parsed = clampHealthValue(Number(value));
    setFilterHealthRange((currentRange) => {
      const min = Math.min(currentRange[0], parsed);
      return [min, parsed];
    });
    setPaginationPage(1);
  }

  function toggleStatus(status: string) {
    setFilterStatus((current) => {
      const next = current.includes(status) ? current.filter((item) => item !== status) : [...current, status];
      return next;
    });
    setPaginationPage(1);
  }

  function toggleCondition(condition: string) {
    setFilterCondition((current) => {
      const next = current.includes(condition) ? current.filter((item) => item !== condition) : [...current, condition];
      return next;
    });
    setPaginationPage(1);
  }

  function updatePage(page: number) {
    const lastPage = Math.max(1, Math.ceil(paginationTotalItems / paginationSize));
    const boundedPage = Math.max(1, Math.min(page, lastPage));
    setPaginationPage(boundedPage);
  }

  function updatePageSize(value: string) {
    const nextSize = Number(value);
    if (Number.isNaN(nextSize)) return;

    setPaginationSize(nextSize);
    setPaginationPage(1);
  }

  const filteredRowsForCsv = table.getFilteredRowModel().rows.map((row) => row.original);
  const paginationLabel = `${(paginationPage - 1) * paginationSize + 1}-${Math.min(
    paginationPage * paginationSize,
    paginationTotalItems
  )} of ${paginationTotalItems}`;

  return (
    <div className="min-h-screen bg-white text-foreground lg:pl-64">
      <main className="space-y-5 px-4 pb-8 pt-20 lg:px-2 lg:pb-10">
        <section className="rounded-xl border border-border/80 bg-background/95 p-4 shadow-sm backdrop-blur-sm lg:p-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="space-y-3">
              <Link
                to={
                  searchQuery === null
                    ? '/inventoryBatteries'
                    : `/inventoryBatteries?searchQuery=${encodeURIComponent(searchQuery)}&showZero=${encodeURIComponent(zeroQty || '')}`
                }
                className="inline-flex"
              >
                <Button variant="outline" className="w-fit gap-2 border-border/70 px-3" type="button">
                  <AiOutlineArrowLeft className="h-4 w-4" />
                  Back
                </Button>
              </Link>
              <div className="space-y-1">
                <h1 className="max-w-[900px] text-xl font-semibold tracking-tight lg:text-2xl" title={inventoryItemHeader.item_description}>
                  {inventoryItemHeader.item_description || 'Battery Inventory'}
                </h1>
                <div className="text-sm text-muted-foreground">
                  SKU: <span className="font-medium text-foreground">{inventoryItemHeader.item_sku || 'N/A'}</span>
                </div>
              </div>
            </div>

            <div className="inline-flex w-fit items-center gap-2 rounded-full border border-border/70 bg-muted/40 px-3 py-1.5 text-xs font-medium text-muted-foreground">
              <span className="inline-block h-2 w-2 rounded-full bg-emerald-500" />
              {paginationTotalItems > 0 ? `${paginationTotalItems} battery records` : 'No records'}
            </div>
          </div>
        </section>

        <Card className="border-border/80 shadow-sm">
          <CardHeader className="pb-2">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <CardTitle className="text-base">Filters and Table Controls</CardTitle>
                {/* <p className="text-xs text-muted-foreground">Refine results, then export or adjust visible columns.</p> */}
              </div>
              <Button
                type="button"
                variant="destructive"
                size="sm"
                onClick={resetFilters}
                disabled={!hasActiveFilters}
                className={hasActiveFilters ? 'opacity-100' : 'pointer-events-none opacity-0'}
              >
                Clear Filters
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4 pt-2">
            <div className="grid gap-4 lg:grid-cols-12">
              <div className="flex w-full flex-col gap-2 rounded-lg border border-border/70 bg-muted/20 p-3 lg:col-span-3">
                <Label htmlFor="global-search">Search Serial Number</Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="global-search"
                    value={searchText}
                    onChange={(event) => setSearchText(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') applySearch();
                    }}
                    placeholder="Search by S/N"
                  />
                  <Button type="button" variant="default" onClick={applySearch} className="shrink-0 px-3 shadow-sm">
                    <FiSearch className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <div ref={statusFilterRef} className="relative space-y-2 rounded-lg border border-border/70 bg-muted/20 p-3 lg:col-span-3">
                <div className="flex items-center justify-between">
                  <Label htmlFor="status-filter-trigger">Status</Label>
                  <Button
                    id="status-filter-trigger"
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setShowStatusFilters((current) => !current)}
                    className="h-8 gap-1 border-border/70 bg-background px-2"
                  >
                    <FiFilter className="h-4 w-4" />
                    {filterStatus.length}
                    <FiChevronDown className="h-4 w-4" />
                  </Button>
                </div>
                {showStatusFilters && (
                  <div className="absolute left-3 right-3 top-[calc(100%-0.2rem)] z-30 max-h-56 space-y-2 overflow-auto rounded-md border border-border/70 bg-background p-3 shadow-lg">
                    <button
                      type="button"
                      className="text-xs font-medium text-primary underline-offset-4 hover:underline"
                      onClick={() => {
                        setFilterStatus(STATUS_OPTIONS);
                        setPaginationPage(1);
                      }}
                    >
                      Select all
                    </button>
                    {STATUS_OPTIONS.map((status) => (
                      <label key={status} className="flex cursor-pointer items-center gap-2 text-sm">
                        <input type="checkbox" checked={filterStatus.includes(status)} onChange={() => toggleStatus(status)} className="h-4 w-4" />
                        <span>{status}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              <div className="space-y-2 rounded-lg border border-border/70 bg-muted/20 p-3 lg:col-span-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="condition-filter-trigger">Condition</Label>
                  <Button
                    id="condition-filter-trigger"
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setShowConditionFilters((current) => !current)}
                    className="h-8 gap-1 border-border/70 bg-background px-2"
                  >
                    <FiFilter className="h-4 w-4" />
                    {filterCondition.length}
                    <FiChevronDown className="h-4 w-4" />
                  </Button>
                </div>
                {showConditionFilters && (
                  <div className="space-y-2 rounded-md border border-border/70 bg-background p-3">
                    {CONDITION_OPTIONS.map((condition) => (
                      <label key={condition} className="flex cursor-pointer items-center gap-2 text-sm">
                        <input type="checkbox" checked={filterCondition.includes(condition)} onChange={() => toggleCondition(condition)} className="h-4 w-4" />
                        <span>{condition}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              <div className="space-y-2 rounded-lg border border-border/70 bg-muted/20 p-3 lg:col-span-2">
                <Label htmlFor="health-min">Battery Health Range (0-100)</Label>
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    id="health-min"
                    type="number"
                    min={0}
                    max={100}
                    value={filterHealthRange[0]}
                    onChange={(event) => updateHealthMin(event.target.value)}
                  />
                  <Input
                    id="health-max"
                    type="number"
                    min={0}
                    max={100}
                    value={filterHealthRange[1]}
                    onChange={(event) => updateHealthMax(event.target.value)}
                  />
                </div>
                <div className="space-y-2 rounded-md border border-border/70 bg-background p-3">
                  <input
                    type="range"
                    min={0}
                    max={100}
                    value={filterHealthRange[0]}
                    onChange={(event) => updateHealthMin(event.target.value)}
                    className="w-full"
                  />
                  <input
                    type="range"
                    min={0}
                    max={100}
                    value={filterHealthRange[1]}
                    onChange={(event) => updateHealthMax(event.target.value)}
                    className="w-full"
                  />
                  <p className="text-xs text-muted-foreground">
                    Current: {filterHealthRange[0]}% to {filterHealthRange[1]}%
                  </p>
                </div>
              </div>

              <div className="space-y-2 rounded-lg border border-border/70 bg-muted/20 p-3 lg:col-span-2">
                <Label htmlFor="column-visibility-trigger">Columns and Export</Label>
                <div className="flex flex-wrap gap-2">
                  <Button
                    id="column-visibility-trigger"
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setShowColumnMenu((current) => !current)}
                    className="gap-1 border-border/70 bg-background"
                  >
                    <FiChevronDown className="h-4 w-4" />
                    Columns
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => exportRowsToCsv(filteredRowsForCsv, `${inventoryItemHeader.item_sku || 'inventory-batteries'}.csv`)}
                    className="gap-2 border-border/70 bg-background"
                  >
                    <AiOutlineDownload className="h-4 w-4" />
                    CSV
                  </Button>
                  <Button type="button" variant="outline" size="sm" onClick={fetchInventory} className="gap-2 border-border/70 bg-background">
                    <AiOutlineReload className="h-4 w-4" />
                    Refresh
                  </Button>
                </div>
                {showColumnMenu && (
                  <div className="max-h-48 space-y-2 overflow-auto rounded-md border border-border/70 bg-background p-3">
                    {table
                      .getAllLeafColumns()
                      .filter((column) => column.id !== 'select')
                      .map((column) => (
                        <label key={column.id} className="flex cursor-pointer items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={column.getIsVisible()}
                            onChange={(event) => column.toggleVisibility(event.target.checked)}
                            className="h-4 w-4"
                          />
                          <span>{typeof column.columnDef.header === 'string' ? column.columnDef.header : column.id}</span>
                        </label>
                      ))}
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border/80 shadow-sm">
          <CardContent className="p-0">
            {loading && (
              <div className="space-y-3 p-4">
                <div className="h-10 animate-pulse rounded-md bg-muted" />
                <div className="h-10 animate-pulse rounded-md bg-muted" />
                <div className="h-10 animate-pulse rounded-md bg-muted" />
                <div className="h-10 animate-pulse rounded-md bg-muted" />
              </div>
            )}
            {!loading && errorMessage && (
              <div className="flex flex-col items-center justify-center gap-3 p-8 text-center">
                <p className="text-sm text-destructive">{errorMessage}</p>
                <Button type="button" variant="outline" onClick={fetchInventory} className="gap-2">
                  <AiOutlineReload className="h-4 w-4" />
                  Retry
                </Button>
              </div>
            )}
            {!loading && !errorMessage && table.getRowModel().rows.length === 0 && (
              <div className="p-8 text-center text-sm text-muted-foreground">No batteries found for the selected filters.</div>
            )}
            {!loading && !errorMessage && table.getRowModel().rows.length > 0 && (
              <div className="max-h-[calc(100vh-26rem)] overflow-auto">
                <Table className="min-w-[1200px]">
                  <TableHeader className="sticky top-0 z-10 border-b bg-muted/50 backdrop-blur-sm">
                    {table.getHeaderGroups().map((headerGroup) => (
                      <TableRow key={headerGroup.id}>
                        {headerGroup.headers.map((header) => (
                          <TableHead key={header.id} style={{ width: `${header.getSize()}px` }} className="relative whitespace-nowrap py-3">
                            {!header.isPlaceholder && (
                              <button
                                type="button"
                                className={
                                  header.column.getCanSort()
                                    ? 'flex items-center gap-2 text-left text-[13px] font-semibold tracking-wide text-foreground/90'
                                    : 'text-left text-[13px] font-semibold tracking-wide text-foreground/90'
                                }
                                onClick={header.column.getToggleSortingHandler()}
                              >
                                {flexRender(header.column.columnDef.header, header.getContext())}
                                {header.column.getCanSort() && (
                                  <span className="text-xs text-muted-foreground">{getSortDirectionLabel(header.column.getIsSorted())}</span>
                                )}
                              </button>
                            )}
                            {header.column.getCanResize() && (
                              <button
                                type="button"
                                aria-label={`Resize ${String(header.column.columnDef.header ?? header.column.id)} column`}
                                onMouseDown={header.getResizeHandler()}
                                onTouchStart={header.getResizeHandler()}
                                className="absolute right-0 top-0 h-full w-1 cursor-col-resize touch-none select-none bg-transparent hover:bg-primary/40"
                              />
                            )}
                          </TableHead>
                        ))}
                      </TableRow>
                    ))}
                  </TableHeader>
                  <TableBody>
                    {table.getRowModel().rows.map((row) => (
                      <TableRow key={row.id} className="border-b border-border/60 transition-colors hover:bg-muted/40">
                        {row.getVisibleCells().map((cell) => (
                          <TableCell key={cell.id} style={{ width: `${cell.column.getSize()}px` }} className="align-top">
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        <section className="flex flex-col gap-3 rounded-xl border border-border/80 bg-background/95 p-4 shadow-sm backdrop-blur-sm lg:flex-row lg:items-center lg:justify-between">
          <div className="text-sm font-medium text-muted-foreground">{paginationTotalItems > 0 ? paginationLabel : '0 results'}</div>

          <div className="flex flex-wrap items-center gap-2">
            <Label htmlFor="page-size" className="whitespace-nowrap">
              Rows per page
            </Label>
            <Select
              id="page-size"
              value={String(paginationSize)}
              onChange={(event) => updatePageSize(event.target.value)}
              className="h-9 w-[90px] border-border/70 bg-background"
            >
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </Select>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => updatePage(paginationPage - 1)}
              disabled={paginationPage <= 1}
              className="border-border/70 bg-background"
            >
              <FiChevronLeft className="h-4 w-4" />
            </Button>
            <span className="min-w-[96px] rounded-md border border-border/70 bg-muted/30 px-3 py-1 text-center text-sm font-medium">Page {paginationPage}</span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => updatePage(paginationPage + 1)}
              disabled={paginationPage >= Math.ceil(paginationTotalItems / paginationSize)}
              className="border-border/70 bg-background"
            >
              <FiChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </section>
      </main>
    </div>
  );
}
